import { useMutation, useSuspenseQuery } from '@apollo/client';
import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useBlocker } from 'react-router-dom';

import { useApiClient } from '@eluve/api-client-provider';
import type { PreviewCandidateAppointment } from '@eluve/api-contract';
import { cacheUtils } from '@eluve/apollo-client';
import {
  DEFAULT_DETAIL_LEVEL,
  DEFAULT_FORMAT,
  LlmOutputPreferencesForm,
} from '@eluve/blocks';
import type { DetailLevel, Format } from '@eluve/blocks';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Content,
  Header,
  Heading,
  Icon,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SidebarTrigger,
  Stack,
  Text,
  useToast,
} from '@eluve/components';
import { graphql } from '@eluve/graphql.tada';
import { useTranslation } from '@eluve/i18n';
import { InsightsPreviewPanel, LoadingInsights } from '@eluve/insights';
import { useNamedLogger } from '@eluve/logger';
import { useAnalytics } from '@eluve/posthog-react';
import {
  useAssignedTenantIdFromParams,
  useUserIdFromSession,
} from '@eluve/session-helpers';
import { useBeforeUnload } from '@eluve/utility-hooks';

import {
  type PreviewSettings,
  previewSettingsKey,
  useInsightsPreviewCache,
} from './useInsightsPreviewCache';
import { usePviCandidateAppointments } from './usePviCandidateAppointments';

const SAMPLE_OPTION_VALUE = '__sample__';

const formatAppointmentDate = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const getInsightsSettingsQuery = graphql(`
  query getInsightsSettings($tenantId: uuid!, $userId: uuid!) {
    tenantUserInsightsSettings(
      where: { tenantId: { _eq: $tenantId }, userId: { _eq: $userId } }
    ) {
      __typename
      tenantId
      userId
      preferenceId
      llmOutputPreference {
        __typename
        id
        tenantId
        format
        detailLevel
        customInstructions
      }
    }
  }
`);

const insertLlmOutputPreferenceMutation = graphql(`
  mutation InsertLlmOutputPreference(
    $format: String!
    $detailLevel: String!
    $customInstructions: String
  ) {
    insertTenantUserLlmOutputPreferencesOne(
      object: {
        format: $format
        detailLevel: $detailLevel
        customInstructions: $customInstructions
      }
    ) {
      __typename
      id
      tenantId
    }
  }
`);

const insertInsightsSettingsMutation = graphql(`
  mutation InsertInsightsSettings($preferenceId: uuid!) {
    insertTenantUserInsightsSettingsOne(
      object: { preferenceId: $preferenceId }
    ) {
      __typename
      tenantId
      userId
      preferenceId
    }
  }
`);

const updateLlmOutputPreferenceMutation = graphql(`
  mutation UpdateLlmOutputPreference(
    $tenantId: uuid!
    $id: uuid!
    $format: String!
    $detailLevel: String!
    $customInstructions: String
  ) {
    updateTenantUserLlmOutputPreferencesByPk(
      pkColumns: { tenantId: $tenantId, id: $id }
      _set: {
        format: $format
        detailLevel: $detailLevel
        customInstructions: $customInstructions
      }
    ) {
      __typename
      id
      tenantId
      format
      detailLevel
      customInstructions
    }
  }
`);

const deleteInsightsSettingsMutation = graphql(`
  mutation DeleteInsightsSettings(
    $tenantId: uuid!
    $userId: uuid!
    $preferenceId: uuid!
  ) {
    deleteTenantUserInsightsSettingsByPk(
      tenantId: $tenantId
      userId: $userId
      preferenceId: $preferenceId
    ) {
      __typename
      tenantId
      userId
      preferenceId
    }
  }
`);

const deleteLlmOutputPreferenceMutation = graphql(`
  mutation DeleteLlmOutputPreference($tenantId: uuid!, $id: uuid!) {
    deleteTenantUserLlmOutputPreferencesByPk(tenantId: $tenantId, id: $id) {
      __typename
      id
      tenantId
    }
  }
`);

export const TenantUserInsightsSettingsPage = () => {
  const tenantId = useAssignedTenantIdFromParams();
  const userId = useUserIdFromSession();
  const analytics = useAnalytics('insights_settings');
  const toast = useToast();
  const apiClient = useApiClient();
  const logger = useNamedLogger('insights_settings');
  const { t } = useTranslation();

  const { data, refetch } = useSuspenseQuery(getInsightsSettingsQuery, {
    variables: { tenantId, userId },
  });

  const existingSettings = data.tenantUserInsightsSettings[0];
  const existingPreference = existingSettings?.llmOutputPreference;

  const initialSettings: PreviewSettings = useMemo(
    () => ({
      appointmentId: null,
      format: (existingPreference?.format as Format) ?? DEFAULT_FORMAT,
      detailLevel:
        (existingPreference?.detailLevel as DetailLevel) ??
        DEFAULT_DETAIL_LEVEL,
      customInstructions: existingPreference?.customInstructions ?? '',
    }),
    [
      existingPreference?.format,
      existingPreference?.detailLevel,
      existingPreference?.customInstructions,
    ],
  );

  const [pending, setPending] = useState<PreviewSettings>(initialSettings);
  const [committed, setCommitted] = useState<PreviewSettings>(initialSettings);

  const [isSaving, setIsSaving] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [eligibleRegenCount, setEligibleRegenCount] = useState(0);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [loadingStartedAt, setLoadingStartedAt] = useState<number | null>(null);
  const [isCompleting, setIsCompleting] = useState(false);

  const { candidates } = usePviCandidateAppointments(tenantId);
  const candidateById = useMemo(() => {
    const map = new Map<string, PreviewCandidateAppointment>();
    if (!candidates) return map;
    for (const c of candidates) {
      map.set(c.appointmentId, c);
    }
    return map;
  }, [candidates]);

  const {
    lookup,
    generate,
    isGenerating,
    error: previewError,
  } = useInsightsPreviewCache({ tenantId });

  const cachedSpec = lookup(committed);
  // Stale when pending differs from committed, OR when we'd render the
  // static fallback (no cached spec) for committed settings that include
  // custom instructions — the fallback can't represent those, so the
  // preview is effectively out of date until Generate runs.
  const isStale =
    previewSettingsKey(pending) !== previewSettingsKey(committed) ||
    (cachedSpec == null && committed.customInstructions.trim() !== '');

  const generateDisabled = !isStale || isGenerating || isSaving;

  const unsavedChanges = existingPreference
    ? pending.format !== existingPreference.format ||
      pending.detailLevel !== existingPreference.detailLevel ||
      (pending.customInstructions ?? '') !==
        (existingPreference.customInstructions ?? '')
    : pending.format !== DEFAULT_FORMAT ||
      pending.detailLevel !== DEFAULT_DETAIL_LEVEL ||
      pending.customInstructions !== '';

  useBeforeUnload(unsavedChanges);

  const blocker = useBlocker(unsavedChanges);

  const [insertPreference] = useMutation(insertLlmOutputPreferenceMutation);
  const [insertSettings] = useMutation(insertInsightsSettingsMutation);
  const [updatePreference] = useMutation(updateLlmOutputPreferenceMutation);
  const [deleteSettings] = useMutation(deleteInsightsSettingsMutation);
  const [deletePreference] = useMutation(deleteLlmOutputPreferenceMutation);

  const updatePending = (patch: Partial<PreviewSettings>) =>
    setPending((prev) => ({ ...prev, ...patch }));

  // Drive the progress-bar fast-forward: when the underlying generation
  // settles (isGenerating flips false), nudge LoadingInsights into its
  // completion animation. The progress bar then animates to 100% and fires
  // its onComplete callback, which clears loadingStartedAt and reveals the
  // InsightsPreviewPanel with the new spec.
  useEffect(() => {
    if (loadingStartedAt !== null && !isGenerating && !isCompleting) {
      setIsCompleting(true);
    }
  }, [isGenerating, loadingStartedAt, isCompleting]);

  // "Free-swap zone": sample patient with no custom instructions. The panel
  // already renders 6 pre-generated variants by format × detail, so toggling
  // between them needs no LLM call and no stale banner. Auto-commit pending →
  // committed so the right pane swaps instantly. As soon as the user types
  // custom instructions or picks a real patient, this predicate flips false
  // and the normal stale-state flow takes over.
  useEffect(() => {
    const inFreeSwapZone =
      pending.appointmentId === null &&
      pending.customInstructions.trim() === '';
    if (
      inFreeSwapZone &&
      previewSettingsKey(pending) !== previewSettingsKey(committed)
    ) {
      setCommitted(pending);
    }
  }, [pending, committed]);

  const handleGenerate = async () => {
    const target = pending;
    // Snapshot the committed key at the moment of click so we can detect
    // whether the free-swap effect (or any other path) moved `committed`
    // mid-flight. If it has, the user has navigated away from `target`
    // and we leave their chosen preview alone rather than clobbering it.
    const committedKeyAtStart = previewSettingsKey(committed);
    setLoadingStartedAt(Date.now());
    setIsCompleting(false);
    const result = await generate(target);
    // Only commit on success. On `error` (network failure or non-200/202),
    // keep `committed` pointing at the previously-rendered settings so the
    // right pane keeps showing the prior preview while the user reads the
    // error message under the Generate button.
    if (result !== 'error') {
      setCommitted((current) =>
        previewSettingsKey(current) === committedKeyAtStart ? target : current,
      );
    }
  };

  const handleLoadingComplete = () => {
    setLoadingStartedAt(null);
    setIsCompleting(false);
  };

  const resetToDefaults = () => {
    setPending({
      appointmentId: pending.appointmentId,
      format: DEFAULT_FORMAT,
      detailLevel: DEFAULT_DETAIL_LEVEL,
      customInstructions: '',
    });
  };

  const handleSave = async () => {
    setIsSaving(true);
    const savedSnapshot = pending;
    try {
      if (existingPreference) {
        await updatePreference({
          variables: {
            tenantId,
            id: existingPreference.id,
            format: pending.format,
            detailLevel: pending.detailLevel,
            customInstructions: pending.customInstructions || null,
          },
        });
        analytics.capture('upserted', {
          tenantId,
          userId,
          format: pending.format,
          detailLevel: pending.detailLevel,
          hasCustomInstructions: Boolean(pending.customInstructions),
        });
      } else {
        const { data: prefData } = await insertPreference({
          variables: {
            format: pending.format,
            detailLevel: pending.detailLevel,
            customInstructions: pending.customInstructions || null,
          },
        });

        const newPrefId = prefData?.insertTenantUserLlmOutputPreferencesOne?.id;
        if (!newPrefId) {
          toast.error(t('settings.insights.savePreferencesFailed'));
          return;
        }

        try {
          await insertSettings({
            variables: { preferenceId: newPrefId },
          });
        } catch {
          // Clean up orphaned preference
          try {
            await deletePreference({
              variables: { tenantId, id: newPrefId },
            });
          } catch {
            // Best-effort cleanup
          }
          toast.error(t('settings.insights.savePreferencesFailed'));
          return;
        }

        analytics.capture('upserted', {
          tenantId,
          userId,
          format: pending.format,
          detailLevel: pending.detailLevel,
          hasCustomInstructions: Boolean(pending.customInstructions),
        });
      }

      await refetch();
      // Lock both pending and committed to the just-saved snapshot. Without
      // this, `committed` could still point at pre-save settings (e.g. user
      // saved without clicking Generate first), leaving the stale banner
      // armed misleadingly. Syncing pending also guards against the
      // useState(initialSettings) initializer staying frozen if the
      // post-refetch existingPreference shape ever drifts.
      setPending(savedSnapshot);
      setCommitted(savedSnapshot);
      toast.success(t('settings.insights.preferencesSaved'));

      try {
        const dryRunResponse =
          await apiClient.tenantAppointmentOutputArtifacts.regenerateUpcomingPrevisitInsights(
            {
              params: { tenantId },
              body: { dryRun: true },
            },
          );
        if (dryRunResponse.status === 200 && dryRunResponse.body.count > 0) {
          setEligibleRegenCount(dryRunResponse.body.count);
        }
      } catch (err) {
        logger.warn('Failed to fetch upcoming appointments for regeneration', {
          error: err,
        });
      }
    } catch {
      toast.error(t('settings.insights.savePreferencesFailed'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleRegenerate = async () => {
    const previewedCount = eligibleRegenCount;
    setIsRegenerating(true);
    setEligibleRegenCount(0);
    analytics.capture('regenerate_today_clicked', {
      tenantId,
      userId,
      count: previewedCount,
    });

    try {
      const response =
        await apiClient.tenantAppointmentOutputArtifacts.regenerateUpcomingPrevisitInsights(
          {
            params: { tenantId },
            body: { dryRun: false },
          },
        );

      if (response.status === 200) {
        const scheduled = response.body.count;
        if (scheduled === 0) {
          toast.info(t('settings.insights.noRegenNeeded'));
        } else {
          toast.success(
            t(
              scheduled === 1
                ? 'settings.insights.regeneratingOne'
                : 'settings.insights.regeneratingMany',
              { count: scheduled },
            ),
          );
        }
      } else {
        toast.error(t('settings.insights.regenerateFailed'));
      }
    } catch (err) {
      logger.warn('Failed to regenerate insights', { error: err });
      toast.error(t('settings.insights.regenerateFailed'));
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleReset = async () => {
    if (!existingSettings || !existingPreference) {
      resetToDefaults();
      setShowResetDialog(false);
      return;
    }

    setIsSaving(true);
    try {
      await deleteSettings({
        variables: {
          tenantId,
          userId,
          preferenceId: existingSettings.preferenceId,
        },
      });

      await deletePreference({
        variables: { tenantId, id: existingPreference.id },
      });

      cacheUtils.evict({
        typeName: 'TenantUserInsightsSettings',
        key: {
          tenantId,
          userId,
          preferenceId: existingSettings.preferenceId,
        },
      });
      cacheUtils.evict({
        typeName: 'TenantUserLlmOutputPreferences',
        key: { tenantId, id: existingPreference.id },
      });

      resetToDefaults();
      setShowResetDialog(false);

      analytics.capture('reset', { tenantId, userId });

      await refetch();
    } catch {
      toast.error(t('settings.insights.resetFailed'));
    } finally {
      setIsSaving(false);
    }
  };

  const committedPatientName = committed.appointmentId
    ? candidateById.get(committed.appointmentId)?.patientName
    : undefined;

  // The progress-bar overlay is shown whenever there's an in-flight
  // generation. It hides itself only after LoadingInsights completes its
  // animation (handleLoadingComplete fires onComplete → setLoadingStartedAt(null)).
  const showLoadingProgress = loadingStartedAt !== null;

  return (
    <>
      <Helmet>
        <title>Insights Preferences | Eluve</title>
      </Helmet>
      <Header>
        <SidebarTrigger />
        <Stack justify="end" gap={2} direction="horizontal">
          <div className="my-2 w-full py-2 xl:pl-3">
            <Heading size="2xl" responsive weight="medium" level={1}>
              {t('settings.insights.title')}
            </Heading>
          </div>
          <Stack fullWidth={false} gap={2} direction="horizontal">
            {Boolean(existingPreference) && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowResetDialog(true)}
                disabled={isSaving}
              >
                <Icon name="RotateCcw" size="auto" />
                {t('settings.common.reset')}
              </Button>
            )}
            <Button
              size="sm"
              onClick={handleSave}
              disabled={!unsavedChanges || isSaving}
            >
              {t('settings.insights.savePreferences')}
            </Button>
          </Stack>
        </Stack>
      </Header>
      <Content>
        <Stack align="stretch" direction="horizontal" fullHeight>
          {/* Left: Settings */}
          <div className="flex-1 overflow-auto p-6">
            <Stack gap={6} className="max-w-2xl">
              <Stack gap={3}>
                <Text>{t('settings.insights.previewAgainst')}</Text>
                <Select
                  value={pending.appointmentId ?? SAMPLE_OPTION_VALUE}
                  onValueChange={(value) =>
                    updatePending({
                      appointmentId:
                        value === SAMPLE_OPTION_VALUE ? null : value,
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={SAMPLE_OPTION_VALUE}>
                      {t('settings.insights.samplePatient')}
                    </SelectItem>
                    {candidates?.map((c) => (
                      <SelectItem key={c.appointmentId} value={c.appointmentId}>
                        {c.patientName} — {formatAppointmentDate(c.startDate)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Stack>

              <LlmOutputPreferencesForm
                format={pending.format}
                detailLevel={pending.detailLevel}
                customInstructions={pending.customInstructions}
                onFormatChange={(format) => updatePending({ format })}
                onDetailLevelChange={(detailLevel) =>
                  updatePending({ detailLevel })
                }
                onCustomInstructionsChange={(customInstructions) =>
                  updatePending({ customInstructions })
                }
              />

              <Stack gap={2} align="start">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    void handleGenerate();
                  }}
                  disabled={generateDisabled}
                >
                  {isGenerating
                    ? t('settings.insights.generatingPreview')
                    : t('settings.insights.generatePreview')}
                </Button>
                {previewError && (
                  <Text size="xs" className="text-text-warning">
                    {previewError}
                  </Text>
                )}
              </Stack>
            </Stack>
          </div>

          {/* Right: Preview */}
          <div className="flex-1 overflow-auto bg-bg-secondary p-6">
            <Stack gap={3} align="stretch">
              {isStale && (
                <Stack
                  direction="horizontal"
                  gap={2}
                  align="center"
                  className="sticky top-0 z-10 rounded-md border border-border-warning-subtle bg-bg-warning-subtle px-3 py-2 shadow-sm"
                >
                  <Icon
                    name="TriangleAlert"
                    size="sm"
                    className="shrink-0 text-text-warning-subtle"
                  />
                  <Text inline size="xs" className="text-text-warning-subtle">
                    {t('settings.insights.previewOutOfDate')}
                  </Text>
                </Stack>
              )}
              {showLoadingProgress ? (
                <div className="rounded-lg bg-bg-primary p-5">
                  <LoadingInsights
                    loadingStartedAt={loadingStartedAt}
                    isCompleting={isCompleting}
                    onComplete={handleLoadingComplete}
                  />
                </div>
              ) : (
                <div className={isStale ? 'opacity-50 transition-opacity' : ''}>
                  <InsightsPreviewPanel
                    format={committed.format}
                    detailLevel={committed.detailLevel}
                    generatedSpec={cachedSpec}
                    isGenerating={false}
                    patientName={committedPatientName}
                  />
                </div>
              )}
            </Stack>
          </div>
        </Stack>
      </Content>

      {/* Navigation Blocker Dialog */}
      <AlertDialog
        open={blocker.state === 'blocked'}
        onOpenChange={(open) => {
          if (!open) blocker.reset?.();
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('settings.insights.unsavedChangesTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('settings.insights.unsavedChangesDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => blocker.reset?.()}>
              {t('settings.insights.stay')}
            </AlertDialogCancel>
            <AlertDialogAction onClick={() => blocker.proceed?.()}>
              {t('settings.insights.leave')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Same-day Regeneration Dialog */}
      <AlertDialog
        open={eligibleRegenCount > 0}
        onOpenChange={(open) => {
          if (!open) setEligibleRegenCount(0);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('settings.insights.regenerateDialogTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                eligibleRegenCount === 1
                  ? 'settings.insights.regenerateDialogDescriptionOne'
                  : 'settings.insights.regenerateDialogDescriptionMany',
                { count: eligibleRegenCount },
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isRegenerating}>
              {t('settings.insights.notNow')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRegenerate}
              disabled={isRegenerating}
            >
              {t('settings.insights.regenerateNow')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset Confirmation Dialog */}
      <AlertDialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('settings.common.resetToDefaultTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('settings.insights.resetDialogDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleReset}>
              {t('settings.common.reset')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
